type StorageProvider = 'localStorage' | 'sessionStorage';

export interface StorageLike {
  getItem(name: string): string | null;

  setItem(name: string, value: string): void;

  removeItem(name: string): void;

  clear(): void;

  key(index: number): string | null;

  readonly length: number;
}

const storageFactory = (provider: StorageProvider): StorageLike => {
  let inMemoryStorage: Record<string, string> = {};

  const isSupported = (): boolean => {
    try {
      const testKey = '__local_storage_test_key__';
      window[provider].setItem(testKey, testKey);
      window[provider].removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  };

  const clear = (): void => {
    if (isSupported()) {
      window[provider].clear();
    } else {
      inMemoryStorage = {};
    }
  };

  const getItem = (name: string): string | null => {
    if (isSupported()) {
      return window[provider].getItem(name);
    }

    if (Object.prototype.hasOwnProperty.call(inMemoryStorage, name)) {
      return inMemoryStorage[name];
    }

    return null;
  };

  const key = (index: number): string | null => {
    if (isSupported()) {
      return window[provider].key(index);
    }

    return Object.keys(inMemoryStorage)[index] || null;
  };

  const removeItem = (name: string): void => {
    if (isSupported()) {
      window[provider].removeItem(name);
    } else {
      delete inMemoryStorage[name];
    }
  };

  const setItem = (name: string, value: string): void => {
    if (isSupported()) {
      window[provider].setItem(name, value);
    } else {
      inMemoryStorage[name] = String(value);
    }
  };

  const getLength = (): number => {
    if (isSupported()) {
      return window[provider].length;
    }

    return Object.keys(inMemoryStorage).length;
  };

  const storage = {
    getItem,
    setItem,
    removeItem,
    clear,
    key
  } as StorageLike;

  Object.defineProperty(storage, 'length', { get: getLength });

  return storage;
};

export default storageFactory;
